import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { devicesApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill } from "@/components/Atoms";
import { Plus, Trash2, Pencil, Search, ArrowRight, Upload } from "lucide-react";
import { toast } from "sonner";
import { useCan, useScopedClientIds, canFor } from "@/lib/rbac";
import { useAuth } from "@/store/auth";

const MAC_RE = /^[0-9A-Fa-f]{12}$/;

function DeviceForm({ initial, clients, types, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initial || { mac: "", name: "", client_id: clients[0]?.id || "", type_id: types[0]?.id || "" }
  );
  const [macError, setMacError] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const mac = form.mac.toUpperCase();
        if (!MAC_RE.test(mac)) {
          setMacError("MAC must be exactly 12 hex characters (e.g. AABBCCDDEEFF)");
          toast.error("Invalid MAC address");
          return;
        }
        setMacError("");
        onSubmit({ ...form, mac });
      }}
      className="space-y-3"
    >
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">MAC (12 hex)</span>
        <input
          data-testid="device-input-mac"
          className={`mt-1.5 w-full border rounded-md px-3 py-2 text-sm font-mono uppercase outline-none focus:ring-1 ${
            macError ? "border-red-400 focus:ring-red-400 focus:border-red-400" : "border-zinc-200 focus:ring-[#3730A3] focus:border-[#3730A3]"
          }`}
          value={form.mac}
          onChange={(e) => { setForm({ ...form, mac: e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 12) }); setMacError(""); }}
          placeholder="AABBCCDDEEFF"
        />
        {macError && (
          <div data-testid="device-mac-error" className="mt-1 text-xs text-red-600 font-mono">{macError}</div>
        )}
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Name</span>
        <input
          data-testid="device-input-name"
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3]"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Client</span>
          <select
            data-testid="device-input-client"
            className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-1 focus:ring-[#3730A3]"
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          >
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Device type</span>
          <select
            data-testid="device-input-type"
            className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white outline-none focus:ring-1 focus:ring-[#3730A3]"
            value={form.type_id}
            onChange={(e) => setForm({ ...form, type_id: e.target.value })}
          >
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="device-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">Save</button>
      </div>
    </form>
  );
}

export default function Devices() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({
    client_id: params.get("client") || "",
    type_id: params.get("type") || "",
    search: "",
  });
  const [modal, setModal] = useState(null);

  const canWrite = useCan("write:devices");
  const scoped = useScopedClientIds(); // operator → [ids], others → null
  const role = useAuth((s) => s.role);
  const clientIds = useAuth((s) => s.clientIds);

  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const types = useQuery({ queryKey: ["device-types"], queryFn: devicesApi.types });
  const devices = useQuery({
    queryKey: ["devices", filters],
    queryFn: () => devicesApi.list(filters),
  });

  const inval = () => qc.invalidateQueries({ queryKey: ["devices"] });
  // Backend expects client_id and type_id as strings (Go-side unmarshaller
  // crashes on numeric values). Coerce here so callers can't forget.
  const castDevice = (d) => ({
    ...d,
    client_id: d.client_id != null ? String(d.client_id) : d.client_id,
    type_id: d.type_id != null ? String(d.type_id) : d.type_id,
  });
  const createM = useMutation({ mutationFn: (data) => devicesApi.create(castDevice(data)), onSuccess: () => { toast.success("Device created"); inval(); setModal(null); } });
  const updateM = useMutation({ mutationFn: ({ id, data }) => devicesApi.update(id, castDevice(data)), onSuccess: () => { toast.success("Device updated"); inval(); setModal(null); } });
  const removeM = useMutation({ mutationFn: devicesApi.remove, onSuccess: () => { toast.success("Device deleted"); inval(); } });

  const clientName = (id) => clients.data?.find((c) => c.id === id)?.name || id;
  const typeName = (id) => types.data?.find((t) => t.id === id)?.name || id;
  // For operators the client picker is pre-filtered to their assigned ids.
  const scopedClients = React.useMemo(() => {
    if (scoped == null) return clients.data || [];
    const set = new Set(scoped);
    return (clients.data || []).filter((c) => set.has(String(c.id)));
  }, [clients.data, scoped]);
  // Same filter applied to the device list itself for operators.
  const visibleDevices = React.useMemo(() => {
    const rows = devices.data || [];
    if (scoped == null) return rows;
    const set = new Set(scoped);
    return rows.filter((d) => set.has(String(d.client_id)));
  }, [devices.data, scoped]);
  const pager = usePager(visibleDevices, { defaultSize: 50, sizes: [25, 50, 100] });
  const canRowWrite = (d) => canFor({ role, clientIds }, "write:devices", d.client_id);

  return (
    <PageContainer>
      <PageHeader
        title="Devices"
        subtitle="BLE & LoRaWAN endpoints registered with the parser layer."
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <button
                data-testid="device-bulk-import"
                onClick={() => setModal({ mode: "bulk" })}
                className="border border-zinc-200 bg-white rounded-md px-3 py-2 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5"
              >
                <Upload className="w-4 h-4" /> Bulk import
              </button>
              <button
                data-testid="device-new"
                onClick={() => setModal({ mode: "create" })}
                className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> New device
              </button>
            </div>
          ) : null
        }
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
            <input
              data-testid="device-search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search by MAC or name…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md outline-none focus:ring-1 focus:ring-[#3730A3]"
            />
          </div>
          <select
            data-testid="device-filter-client"
            value={filters.client_id}
            onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All clients</option>
            {scopedClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            data-testid="device-filter-type"
            value={filters.type_id}
            onChange={(e) => setFilters({ ...filters, type_id: e.target.value })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All types</option>
            {types.data?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">MAC</th>
              <th className="text-left p-3 font-semibold">Client</th>
              <th className="text-left p-3 font-semibold">Type</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {devices.isError && (
              <tr>
                <td colSpan={5} className="p-4">
                  <div
                    data-testid="devices-error"
                    className="border border-red-200 bg-red-50 text-red-800 rounded-md px-3 py-2 text-sm"
                  >
                    <div className="font-mono text-xs uppercase tracking-widest mb-1">Backend error</div>
                    {devices.error?.message || "Failed to load devices"}
                  </div>
                </td>
              </tr>
            )}
            {pager.pageRows.map((d) => (
              <tr key={d.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`device-row-${d.id}`}>
                <td className="p-3 font-medium">{d.name}</td>
                <td className="p-3"><Mono>{d.mac}</Mono></td>
                <td className="p-3 text-zinc-600">{clientName(d.client_id)}</td>
                <td className="p-3"><CodePill>{typeName(d.type_id)}</CodePill></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => navigate(`/devices/${d.id}`)} className="p-1.5 rounded hover:bg-zinc-100" data-testid={`device-open-${d.id}`}><ArrowRight className="w-4 h-4" /></button>
                    {canRowWrite(d) && (
                      <>
                        <button onClick={() => setModal({ mode: "edit", row: d })} className="p-1.5 rounded hover:bg-zinc-100" data-testid={`device-edit-${d.id}`}><Pencil className="w-4 h-4" /></button>
                        <button
                          data-testid={`device-delete-${d.id}`}
                          onClick={() => {
                            if (window.confirm(`Delete device ${d.name}?`)) removeM.mutate(d.id);
                          }}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700"
                        ><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {pager.total === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-sm text-zinc-500">No devices match the filters.</td></tr>
            )}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="device-pager" />
      </div>

      {modal && modal.mode === "bulk" && (
        <BulkImportModal
          clients={scopedClients}
          types={types.data || []}
          onClose={() => setModal(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ["devices"] }); setModal(null); }}
        />
      )}
      {modal && modal.mode !== "bulk" && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">
              {modal.mode === "create" ? "New device" : "Edit device"}
            </h2>
            <DeviceForm
              initial={modal.row}
              clients={scopedClients}
              types={types.data || []}
              onCancel={() => setModal(null)}
              onSubmit={(form) =>
                modal.mode === "create" ? createM.mutate(form) : updateM.mutate({ id: modal.row.id, data: form })
              }
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
}

// --- Bulk import --------------------------------------------------------
// Parses a CSV of `mac,client_id,type_id[,name]` (with or without header row)
// and POSTs each row individually. We validate on-parse so users can preview
// which rows will succeed/fail before firing 500 API calls.

// Accepts flexible header names — most users type them slightly differently.
const HEADER_MAP = {
  mac: ["mac", "mac_address", "macaddress"],
  client_id: ["client_id", "clientid", "client"],
  type_id: ["type_id", "typeid", "type", "device_type", "device_type_id"],
  name: ["name", "device_name"],
};

function normHeader(h) {
  const s = h.trim().toLowerCase();
  for (const [k, aliases] of Object.entries(HEADER_MAP)) {
    if (aliases.includes(s)) return k;
  }
  return null;
}

function parseCsv(text, { clients, types }) {
  const clientIds = new Set(clients.map((c) => String(c.id)));
  const clientNamesToId = Object.fromEntries(clients.map((c) => [c.name.toLowerCase(), String(c.id)]));
  const typeIds = new Set(types.map((t) => String(t.id)));
  const typeNamesToId = Object.fromEntries(types.map((t) => [t.name.toLowerCase(), String(t.id)]));

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], errors: [], headers: null };

  // Sniff for header row — if the first line contains any known header keyword,
  // treat it as headers; otherwise assume positional [mac, client_id, type_id, name].
  const first = lines[0].split(/[,;\t]/).map((c) => c.trim());
  const asHeader = first.map(normHeader);
  const hasHeader = asHeader.some(Boolean);
  const headerCols = hasHeader
    ? asHeader.map((h, i) => h || `col${i}`)
    : ["mac", "client_id", "type_id", "name"].slice(0, first.length);

  const dataLines = hasHeader ? lines.slice(1) : lines;

  const rows = [];
  const errors = [];

  dataLines.forEach((line, i) => {
    const cells = line.split(/[,;\t]/).map((c) => c.trim());
    const raw = Object.fromEntries(headerCols.map((h, j) => [h, cells[j] || ""]));
    const lineNo = (hasHeader ? i + 2 : i + 1);

    const mac = (raw.mac || "").replace(/[^0-9a-fA-F]/g, "").toUpperCase();
    let client_id = raw.client_id;
    let type_id = raw.type_id;
    const name = raw.name || `dev-${mac.slice(-6)}`;

    const rowErrors = [];
    if (!MAC_RE.test(mac)) rowErrors.push(`invalid MAC "${raw.mac}"`);

    // Accept either ID or client/type name (case-insensitive).
    if (!clientIds.has(String(client_id))) {
      const byName = clientNamesToId[String(client_id).toLowerCase()];
      if (byName) client_id = byName;
      else rowErrors.push(`unknown client "${raw.client_id}"`);
    } else {
      client_id = String(client_id);
    }
    if (!typeIds.has(String(type_id))) {
      const byName = typeNamesToId[String(type_id).toLowerCase()];
      if (byName) type_id = byName;
      else rowErrors.push(`unknown device type "${raw.type_id}"`);
    } else {
      type_id = String(type_id);
    }

    const entry = { lineNo, raw: line, mac, client_id, type_id, name, errors: rowErrors };
    if (rowErrors.length) errors.push(entry);
    rows.push(entry);
  });

  return { rows, errors, headers: headerCols };
}

function BulkImportModal({ clients, types, onClose, onDone }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: [] });

  const parsed = React.useMemo(
    () => (text.trim() ? parseCsv(text, { clients, types }) : { rows: [], errors: [], headers: null }),
    [text, clients, types]
  );
  const validRows = parsed.rows.filter((r) => r.errors.length === 0);

  const runImport = async () => {
    if (!validRows.length) { toast.error("No valid rows to import"); return; }
    setRunning(true);
    setProgress({ done: 0, total: validRows.length, failed: [] });
    const failed = [];
    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i];
      try {
        await devicesApi.create({
          mac: r.mac,
          name: r.name,
          client_id: String(r.client_id),
          type_id: String(r.type_id),
        });
      } catch (e) {
        failed.push({ ...r, apiError: e.message || String(e) });
      }
      setProgress({ done: i + 1, total: validRows.length, failed: [...failed] });
    }
    setRunning(false);
    if (failed.length === 0) {
      toast.success(`Imported ${validRows.length} device${validRows.length === 1 ? "" : "s"}`);
      onDone();
    } else {
      toast.error(`${failed.length} of ${validRows.length} failed — see list below`);
    }
  };

  const sample = "mac,client_id,type_id,name\nAABBCC000101,1,1,dev-101\nAABBCC000102,1,1,dev-102";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-heading text-lg font-bold mb-1">Bulk import devices</h2>
        <p className="text-xs text-zinc-500 mb-3">
          Paste a CSV with columns <code className="font-mono">mac, client_id, type_id, name</code> (header row optional). Client and device type accept either the numeric ID or the exact name.
        </p>

        <textarea
          data-testid="bulk-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={sample}
          spellCheck={false}
          rows={8}
          className="w-full font-mono text-[12px] border border-zinc-200 rounded-md p-2 outline-none focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3]"
        />

        <div className="mt-2 flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => setText(sample)}
            className="text-zinc-500 hover:text-zinc-800 underline underline-offset-2"
            data-testid="bulk-load-sample"
          >
            load sample
          </button>
          <span className="text-zinc-400">·</span>
          <span className="text-zinc-500">
            {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} parsed
            {parsed.errors.length > 0 && ` — ${parsed.errors.length} with errors`}
          </span>
        </div>

        {parsed.rows.length > 0 && (
          <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0">
                <tr>
                  <th className="text-left p-2 w-8">#</th>
                  <th className="text-left p-2">MAC</th>
                  <th className="text-left p-2">Client</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody data-testid="bulk-preview">
                {parsed.rows.map((r, i) => {
                  const failed = progress.failed.find((f) => f.mac === r.mac);
                  return (
                    <tr
                      key={i}
                      className={`border-t border-zinc-100 ${r.errors.length ? "bg-red-50/40" : ""}`}
                      data-testid={`bulk-row-${i}`}
                    >
                      <td className="p-2 font-mono text-zinc-400">{r.lineNo}</td>
                      <td className="p-2 font-mono">{r.mac || "—"}</td>
                      <td className="p-2 font-mono">{r.client_id}</td>
                      <td className="p-2 font-mono">{r.type_id}</td>
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">
                        {r.errors.length ? (
                          <span className="text-red-600 font-mono text-[11px]">
                            {r.errors.join(", ")}
                          </span>
                        ) : failed ? (
                          <span className="text-red-600 font-mono text-[11px]">✗ {failed.apiError}</span>
                        ) : (
                          <span className="text-lime-700 font-mono text-[11px]">ready</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {running && (
          <div className="mt-3 text-xs text-zinc-500" data-testid="bulk-progress">
            Importing… {progress.done} / {progress.total}
            <div className="mt-1 h-1 bg-zinc-100 rounded overflow-hidden">
              <div
                className="h-full bg-[#3730A3] transition-all"
                style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-zinc-200">
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            {running ? "Close (running…)" : "Close"}
          </button>
          <button
            type="button"
            data-testid="bulk-run"
            onClick={runImport}
            disabled={running || validRows.length === 0}
            className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 disabled:opacity-40"
          >
            {running
              ? `Importing (${progress.done}/${progress.total})…`
              : `Import ${validRows.length} device${validRows.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
